/**
 * Leitura de datas e CNPJs no texto de certidões e atestados (D66).
 * Regras derivadas dos textos reais das certidões da Sobral Nunes (20/10/2026):
 *  - "Emitida às 13:46:02 do dia 21/07/2026" / "Válida até 17/01/2027."            (CND federal)
 *  - "emitida em 07/10/2026 às 09:25:55, válida até 06/11/2026"                     (CND estadual)
 *  - "Esta certidão será válida até 26/11/2026"                                       (CND municipal)
 *  - "Expedição: 22/09/2026" / "Validade: 21/03/2027"                                 (CNDT)
 *  - "Validade: 19/10/2026 a 17/11/2026"                                              (CRF FGTS)
 *  - "expedida ... em 19/10/2026 e válida até 18/11/2026"                              (falência)
 *  - "emitida automaticamente em 22/07/2026" / "Emitido no dia 04/08/2026"           (Jucese, cartão CNPJ)
 *  - "emitido eletronicamente via Internet em terça, 04 de agosto de 2026"           (alvará)
 * O resultado é sugestão: a pessoa confere antes de gravar.
 */
import { cnpjValido } from "./documentos";

const MESES: Record<string, string> = {
  janeiro: "01", fevereiro: "02", marco: "03", março: "03", abril: "04", maio: "05", junho: "06",
  julho: "07", agosto: "08", setembro: "09", outubro: "10", novembro: "11", dezembro: "12",
};

/** "17/01/2027" → "2027-01-17" (valida dia e mês). */
function isoDeBarra(data: string): string | null {
  const m = data.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mes, a] = m;
  if (Number(d) < 1 || Number(d) > 31 || Number(mes) < 1 || Number(mes) > 12) return null;
  return `${a}-${mes}-${d}`;
}

/** "04 de agosto de 2026" → "2026-08-04". */
function isoDeExtenso(dia: string, mes: string, ano: string): string | null {
  const m = MESES[mes.toLowerCase()];
  if (!m) return null;
  return `${ano}-${m}-${dia.padStart(2, "0")}`;
}

const DATA = String.raw`(\d{2}\/\d{2}\/\d{4})`;

export interface DatasDocumento {
  emissao: string | null;
  validade: string | null;
}

/** Emissão e validade encontradas no texto (formato yyyy-mm-dd), ou nulas. */
/** Emissão compatível com a validade: não posterior a ela e no máximo 400 dias antes. */
function coerente(emissao: string, validade: string): boolean {
  const dias = (Date.parse(validade) - Date.parse(emissao)) / 86_400_000;
  return dias >= 0 && dias <= 400;
}

function somarDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

export function extrairDatasDocumento(textoOriginal: string): DatasDocumento {
  const texto = textoOriginal.replace(/\s+/g, " ");
  let validade: string | null = null;
  let emissao: string | null = null;

  // "Validade: 19/10/2026 a 17/11/2026" (período): início = emissão, fim = validade.
  const periodo = texto.match(new RegExp(String.raw`validade\s*:?\s*` + DATA + String.raw`\s*(?:a|até|ate)\s*` + DATA, "i"));
  if (periodo?.[1] && periodo[2]) {
    emissao = isoDeBarra(periodo[1]);
    validade = isoDeBarra(periodo[2]);
  }

  if (!validade) {
    const m =
      texto.match(new RegExp(String.raw`v[áa]lid[ao]\s+at[ée]\s*(?:o\s+dia\s*)?` + DATA, "i")) ??
      texto.match(new RegExp(String.raw`validade\s*:?\s*` + DATA, "i")) ??
      texto.match(new RegExp(String.raw`vencimento\s*:?\s*` + DATA, "i"));
    if (m?.[1]) validade = isoDeBarra(m[1]);
  }

  if (!emissao) {
    // Todas as datas após "emitida/expedida/emissão": o texto pode citar antes uma norma
    // ("emitida via Internet nos termos da portaria Nº 283 de 15/02/2001") e só depois a emissão real.
    const candidatas = [
      ...texto.matchAll(new RegExp(String.raw`(?:emitid[ao]|expedid[ao]|emiss[ãa]o|expedi[çc][ãa]o)\b.{0,80}?` + DATA, "gi")),
    ]
      .map((m) => (m[1] ? isoDeBarra(m[1]) : null))
      .filter((d): d is string => d !== null);
    const limite = validade;
    emissao = limite ? (candidatas.filter((d) => coerente(d, limite)).pop() ?? null) : (candidatas[0] ?? null);
  }
  if (!emissao) {
    const m = texto.match(/(?:emitid[ao]|expedid[ao])\b[^0-9]{0,80}?(\d{1,2}) de ([a-zç]+) de (\d{4})/i);
    if (m?.[1] && m[2] && m[3]) emissao = isoDeExtenso(m[1], m[2], m[3]);
  }

  // "válida por 30 (trinta) dias" (prazo contado da emissão, como na certidão de falência do TJSE).
  if (!validade && emissao) {
    const prazo = texto.match(/v[áa]lid[ao]\s+(?:pelo\s+prazo\s+de\s+|por\s+)(\d{1,3})\s*(?:\([^)]*\)\s*)?dias/i);
    if (prazo?.[1]) validade = somarDias(emissao, Number(prazo[1]));
  }

  if (emissao && validade && validade < emissao) validade = null;
  // Data de emissão muito anterior à validade costuma ser de norma citada no texto ("Portaria ... de 28/06/2007").
  if (emissao && validade && !coerente(emissao, validade)) emissao = null;
  return { emissao, validade };
}

/**
 * CNPJs encontrados no texto, sem repetição. Só entram números com dígitos verificadores válidos
 * (números de processo no formato 00.000.009/2024-59 não são CNPJ). Os que aparecem logo após a palavra
 * "CNPJ" vêm primeiro; os demais seguem na ordem do texto.
 */
export function extrairCnpjs(texto: string): string[] {
  const rotulados: string[] = [];
  const outros: string[] = [];
  for (const m of texto.matchAll(/\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}/g)) {
    const d = m[0].replace(/\D/g, "");
    if (d.length !== 14 || !cnpjValido(d) || rotulados.includes(d) || outros.includes(d)) continue;
    const antes = texto.slice(Math.max(0, (m.index ?? 0) - 25), m.index ?? 0);
    if (/c\.?\s*n\.?\s*p\.?\s*j/i.test(antes)) rotulados.push(d);
    else outros.push(d);
  }
  return [...rotulados, ...outros];
}

/** Data de emissão de um atestado: a última data por extenso ("Aracaju, 26 de maio de 2026") ou numérica do texto. */
export function extrairDataAtestado(textoOriginal: string): string | null {
  const texto = textoOriginal.replace(/\s+/g, " ");
  const extensos = [...texto.matchAll(/(\d{1,2}) de ([a-zç]+) de (\d{4})/gi)]
    .map((m) => (m[1] && m[2] && m[3] ? isoDeExtenso(m[1], m[2], m[3]) : null))
    .filter((d): d is string => d !== null);
  if (extensos.length > 0) return extensos[extensos.length - 1] ?? null;
  const numericas = [...texto.matchAll(/(\d{2}\/\d{2}\/\d{4})/g)]
    .map((m) => (m[1] ? isoDeBarra(m[1]) : null))
    .filter((d): d is string => d !== null);
  return numericas.length > 0 ? (numericas[numericas.length - 1] ?? null) : null;
}
